import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { 
  Table, 
  Input, 
  Select,  
  Tag, 
  Modal, 
  message, 
  Button, 
  Dropdown, 
  Menu, 
  Tooltip 
} from "antd";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { RxListBullet } from "react-icons/rx";
import { deleteRewardType, getRewardTypes } from "../../../api/Services";
import PageHeader from "../../../components/pageheader/PageHeader";
import EmptyState from "../../../components/emptystate/EmptyState";
import { getRewardTypesBasePath } from "../../../helpers/RewardsPathHelper";

import styles from "./RewardTypes.module.scss";

const RewardTypes = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = getRewardTypesBasePath(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState(searchParams.get("name") || "");
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [activeFilterValue, setActiveFilterValue] = useState<number | undefined>(
    searchParams.get("active") ? parseInt(searchParams.get("active")!) : undefined
  );

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_VEHICLES)) {
      const pageFromUrl = searchParams.get("page");
      if (pageFromUrl) {
        setActualPage(parseInt(pageFromUrl));
      }
      fetchRewardTypes();
    }
  }, [debouncedNameFilterValue, activeFilterValue, searchParams]);

  const fetchRewardTypes = async (pagination = actualPage) => {
    const requestId = beginRequest();
    const response = await getRewardTypes(pagination, {
      name: debouncedNameFilterValue,
      active: activeFilterValue,
    });

    if (isStaleRequest(requestId)) return;

    setData(response.rewardTypes);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  const updateURLParams = (filters: any) => {
    const params = new URLSearchParams();
    if (filters.name) params.set("name", filters.name);
    if (filters.active !== undefined) params.set("active", filters.active.toString());
    if (filters.page && filters.page > 1) params.set("page", filters.page.toString());
    
    setSearchParams(params);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURLParams({
      name: nameFilterValue,
      active: activeFilterValue,
      page: page
    });
    fetchRewardTypes(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setActiveFilterValue(undefined);
    setActualPage(1);
    setSearchParams(new URLSearchParams());
    fetchRewardTypes(1);
  };

  const hasActiveFilters = () => {
    return nameFilterValue !== "" || activeFilterValue !== undefined;
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return (
        <EmptyState
          type="noResults"
          entityName="RewardType"
        />
      );
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="RewardType"
          createPath={`${basePath}/create`}
          createPermission={privilegesEnum.WRITE_REWARDS}
        />
      );
    }

    return null;
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteRewardType(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchRewardTypes();
    setLoading(false);
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a
          href={`${basePath}/${record.id}/view?${searchParams.toString()}`}
          className={styles.nameLink}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>
          {status ? t("Common-Active") : t("Common-Inactive")}
        </Tag>
      ),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`${basePath}/${record.id}/view?${searchParams.toString()}`)}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_REWARDS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`${basePath}/${record.id}/view?${searchParams.toString()}`)}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_REWARDS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];
  

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-RewardTypes")}
        icon={<RxListBullet />}
        onCreateClick={() => navigate(`${basePath}/create`)}
        createPermission={privilegesEnum.WRITE_REWARDS}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(1);
                    updateURLParams({
                      name: e.target.value,
                      active: activeFilterValue,
                      page: 1
                    });
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={activeFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-SearchByState")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setActiveFilterValue(option.value);
                      setActualPage(1);
                      updateURLParams({
                        name: nameFilterValue,
                        active: option.value,
                        page: 1
                      });
                    }
                  }}
                  onClear={() => {
                    setActiveFilterValue(undefined);
                    setActualPage(1);
                    updateURLParams({
                      name: nameFilterValue,
                      active: undefined,
                      page: 1
                    });
                  }}
                >
                  <Select.Option value={0}>
                    {t("Common-Inactive")}
                  </Select.Option>
                  <Select.Option value={1}>{t("Common-Active")}</Select.Option>
                </Select>
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          <Table
            size="small"
            className={styles.table}
            rowKey={(record) => record.id}
            loading={loading}
            columns={columns}
            dataSource={data}
            pagination={{
              total: totalSize,
              showSizeChanger: false,
              onChange: handlePagination,
              current: actualPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: getEmptyState(),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RewardTypes;
